import { bizDevelopmentDashboardService } from "../../../services/api/biz-development-dashboard.endpoint";
import {
  getConversionsUploadDateRange,
  getDateRangeForTimeframe,
} from "../../../components/BizDevelopment/Dashboard/helpers/bizDevelopmentDashboardHelpers";
import {
  setMobileIsLoading,
  setMobileOutputMetadata,
  setMobileQueryOutput,
  setMobileIsComparisonLoading,
  setMobileComparisonQueryOutput,
  setMobileComparisonFetchedFor,
  setOverviewAccountSummaryData,
  setDimensionsMobile,
  setOverviewConversionsActivityNotesDataMobile,
  setOverviewConversionsFilterDataByIdMobile,
  setOverviewConversionsGeoDataMobile,
  setOverviewConversionsMobileData,
  setOverviewConversionsTrafficSourceDataMobile,
  setBudgetMobileDataOfBizDevelopment,
  setBudgetMobileMetaDataOfBizDevelopment,
  setBudgetMobileIsLoading,
  setBudgetBrandPerformanceLastRunData,
  setBeAwareFraudPlayersMobileIsLoading,
  setBeAwareFraudPlayersMobileOutput,
  setBeAwareNegativeBalanceMobileIsLoading,
  setBeAwareNegativeBalanceMobileOutput,
  setBeAwareConversionsUploadMobileIsLoading,
  setBeAwareConversionsUploadMobileOutput,
} from "../../../features/bizDevelopmentDashboardMobileSlice";
import {
  BIZ_BE_AWARE_QUERY_IDS,
  BIZ_DEVELOPMENT_DASHBOARD_QUERY_ID,
} from "../../../pages/BizDevelopment/Dashboard/QueryIDsBizDevelopmentDashboard";
import store from "../../../store";
import {
  setAllUsers,
  setDisplayErrorSnackBar,
} from "../../../features/userSlice";
import { bizDevelopmentDashboardMobileService } from "../../../services/mobileAPI/biz-dev-dashboard.endpoints";

async function getDashboardData(
  oauthTokenHeader: string,
  siteSharedUids: string[],
  timeframe: string,
  customRange?: { startDate: string; endDate: string },
) {
  try {
    store.dispatch(setMobileIsLoading(true));
    const { startDate, endDate } =
      timeframe === "custom" && customRange
        ? customRange
        : getDateRangeForTimeframe(timeframe);
    const response = await bizDevelopmentDashboardService.getDashboardData(
      oauthTokenHeader,
      BIZ_DEVELOPMENT_DASHBOARD_QUERY_ID,
      startDate,
      endDate,
      siteSharedUids,
    );
    if (!response || !response.status || response.status !== 200) {
      throw new Error("Failed to fetch biz development dashboard data");
    } else {
      store.dispatch(
        setMobileQueryOutput(response?.data?.data?.query_output ?? []),
      );
      store.dispatch(
        setMobileOutputMetadata(response?.data?.data?.output_metadata ?? {}),
      );
    }
  } catch (e: any) {
    store.dispatch(
      setDisplayErrorSnackBar({
        message: e?.parsedText || "Unknown error occurred",
        type: "error",
      }),
    );
  } finally {
    store.dispatch(setMobileIsLoading(false));
  }
}

async function getComparisonDashboardData(
  oauthTokenHeader: string,
  siteSharedUids: string[],
  timeframe: string,
  customRange?: { startDate: string; endDate: string },
) {
  try {
    store.dispatch(setMobileIsComparisonLoading(true));
    const { startDate, endDate } =
      timeframe === "custom" && customRange
        ? customRange
        : getDateRangeForTimeframe(timeframe);
    const response = await bizDevelopmentDashboardService.getDashboardData(
      oauthTokenHeader,
      BIZ_DEVELOPMENT_DASHBOARD_QUERY_ID,
      startDate,
      endDate,
      siteSharedUids,
    );
    if (!response || !response.status || response.status !== 200) {
      throw new Error(
        "Failed to fetch biz development dashboard comparison data",
      );
    } else {
      store.dispatch(
        setMobileComparisonQueryOutput(
          response?.data?.data?.query_output ?? [],
        ),
      );
      store.dispatch(setMobileComparisonFetchedFor(timeframe));
    }
  } catch (e: any) {
    store.dispatch(
      setDisplayErrorSnackBar({
        message: e?.parsedText || "Unknown error occurred",
        type: "error",
      }),
    );
  } finally {
    store.dispatch(setMobileIsComparisonLoading(false));
  }
}

async function getOverviewAccountSummaryDataInBizLeaderDashboardMobile({
  oauthTokenHeader,
  queryId,
  websiteSharedUids,
  type,
  dimension,
  period_type,
}: {
  oauthTokenHeader: string;
  queryId: string;
  websiteSharedUids: string[];
  type: string;
  dimension: string[];
  period_type?: string;
}) {
  try {
    const response =
      await bizDevelopmentDashboardMobileService.getOverviewAccountSummaryDataInBizLeaderDashboardMobile(
        {
          oauthTokenHeader,
          queryId,
          websiteSharedUids,
          type,
          dimension,
          period_type,
        },
      );
    if (!response.status || response.status !== 200) {
      throw new Error();
    }
    store.dispatch(setOverviewAccountSummaryData(response?.data?.data || []));
  } catch (e: any) {
    store.dispatch(
      setDisplayErrorSnackBar({
        message:
          e?.parsedText ||
          "Failed to fetch Website query data. Please try again later or Refresh the page.",
        type: "error",
      }),
    );
  }
}

async function getDimensionInBizDashboardMobile(oauthTokenHeader: any) {
  try {
    const response =
      await bizDevelopmentDashboardMobileService.getDimensionInBizDashboardMobile(
        oauthTokenHeader,
      );
    if (!response || !response.status || response.status !== 200) {
      throw new Error("Failed to get dimensions list");
    } else {
      store.dispatch(setDimensionsMobile(response?.data?.data));
    }
    return response;
  } catch (error: any) {
    store.dispatch(
      setDisplayErrorSnackBar({
        message: error?.parsedText || "Unknown error occurred",
        type: "error",
      }),
    );
  }
}

async function getOverviewConversionsMobileData(params: any) {
  try {
    const response =
      await bizDevelopmentDashboardMobileService.getOverviewConversionsMobileData(
        params,
      );
    if (!response?.status || response.status !== 200) {
      throw new Error("API failed");
    }
    store.dispatch(setOverviewConversionsMobileData(response.data?.data || []));
  } catch (e: any) {
    store.dispatch(
      setDisplayErrorSnackBar({
        message:
          e?.parsedText ||
          "Failed to fetch Overview Conversions data. Please try again later or refresh the page.",
        type: "error",
      }),
    );
  }
}

async function getOverviewConversionsTrafficSourcesMobile(
  oauthTokenHeader: string,
) {
  try {
    const response =
      await bizDevelopmentDashboardMobileService.getOverviewConversionsTrafficSourcesMobile(
        oauthTokenHeader,
      );
    if (!response.status || response.status !== 200) {
      throw new Error();
    }
    store.dispatch(
      setOverviewConversionsTrafficSourceDataMobile(response?.data?.data),
    );
  } catch (e: any) {
    store.dispatch(
      setDisplayErrorSnackBar({
        message: e?.parsedText || "Unknown error occurred",
        type: "error",
      }),
    );
  }
}

async function getOverviewConversionsGeosMobile(oauthTokenHeader: string) {
  try {
    const response =
      await bizDevelopmentDashboardMobileService.getOverviewConversionsGeosMobile(
        oauthTokenHeader,
      );
    if (!response.status || response.status !== 200) {
      throw new Error();
    }
    store.dispatch(setOverviewConversionsGeoDataMobile(response?.data?.data));
  } catch (e: any) {
    store.dispatch(
      setDisplayErrorSnackBar({
        message: e?.parsedText || "Unknown error occurred",
        type: "error",
      }),
    );
  }
}

async function getOverviewConversionsFilterOptionsDataForCampaignAdgroupKeyword({
  queryId,
  oauthTokenHeader,
  type,
  key,
  websiteSharedUids = [],
  dimensions = [],
  trafficSource,
  account_shared_uid,
  campaign_status,
}: {
  queryId: any;
  oauthTokenHeader: any;
  type: any;
  key: any;
  websiteSharedUids: any[];
  dimensions: any[];
  trafficSource: any;
  account_shared_uid?: any;
  campaign_status?: any;
}) {
  try {
    const response =
      await bizDevelopmentDashboardMobileService.getOverviewConversionsFilterOptionsDataForCampaignAdgroupKeyword(
        queryId,
        oauthTokenHeader,
        type,
        websiteSharedUids,
        dimensions,
        trafficSource,
        account_shared_uid,
        campaign_status,
      );
    if (!response.status || response.status !== 200) {
      throw new Error();
    }
    store.dispatch(
      setOverviewConversionsFilterDataByIdMobile({
        key,
        data: response?.data?.data || [],
      }),
    );
  } catch (e: any) {
    store.dispatch(
      setDisplayErrorSnackBar({
        message:
          e?.parsedText ||
          "Failed to fetch filter options. Please try again later or Refresh the page.",
        type: "error",
      }),
    );
  }
}

async function getOverviewConversionsActivityNotesMobile({
  oauthTokenHeader,
  websiteSharedUid,
  fromDate,
  toDate,
}: {
  oauthTokenHeader: string;
  websiteSharedUid: any;
  fromDate: string;
  toDate: string;
}) {
  try {
    const response =
      await bizDevelopmentDashboardMobileService.getOverviewConversionsActivityNotesMobile(
        oauthTokenHeader,
        websiteSharedUid,
        fromDate,
        toDate,
      );
    if (response.status === 204) {
      store.dispatch(
        setDisplayErrorSnackBar({
          message: "",
          type: "warning",
        }),
      );
      return;
    }
    if (!response.status || response.status !== 200) {
      throw new Error();
    }
    store.dispatch(
      setOverviewConversionsActivityNotesDataMobile(
        Object.values(response?.data?.data || {}).flat(),
      ),
    );
  } catch (e: any) {
    store.dispatch(
      setDisplayErrorSnackBar({
        message: e?.parsedText || "Failed to load activity notes",
        type: "error",
      }),
    );
  }
}

async function getOverviewConversionsAllUserList(oauthTokenHeader: any) {
  try {
    const response =
      await bizDevelopmentDashboardMobileService.getOverviewConversionsAllUsers(
        oauthTokenHeader,
      );
    if (!response || !response.status || response.status !== 200) {
      throw new Error("Failed to get users");
    }
    store.dispatch(setAllUsers(response?.data?.data));
  } catch (e: any) {
    store.dispatch(
      setDisplayErrorSnackBar({
        message: e?.parsedText || "Unknown error occurred",
        type: "error",
      }),
    );
  }
}

async function getBizMobileBudgetData({
  oauthTokenHeader,
  queryId,
  websiteSharedUids,
  type,
  dimension,
}: {
  oauthTokenHeader: string;
  queryId: string;
  websiteSharedUids: string[];
  type: string;
  dimension: string[];
}) {
  try {
    store.dispatch(setBudgetMobileIsLoading(true));
    const response =
      await bizDevelopmentDashboardMobileService.getBizMobileBudgetData({
        oauthTokenHeader,
        queryId,
        websiteSharedUids,
        type,
        dimension,
      });
    if (!response || !response.status) {
      throw new Error("Failed to fetch biz development budget data");
    } else if (response.status === 200) {
      store.dispatch(
        setBudgetMobileDataOfBizDevelopment(
          response?.data?.data?.query_output ?? [],
        ),
      );
      store.dispatch(
        setBudgetMobileMetaDataOfBizDevelopment(
          response?.data?.data?.output_metadata ?? {},
        ),
      );
    }
  } catch (e: any) {
    store.dispatch(
      setDisplayErrorSnackBar({
        message: e?.parsedText || "Unable to fetch budget data",
        type: "error",
      }),
    );
  } finally {
    store.dispatch(setBudgetMobileIsLoading(false));
  }
}

async function getBudgetCardBrandLastRunQueryInBizDevDashboardMobile({
  oauthTokenHeader,
  queryId,
  websiteSharedUids,
  type,
  dimension,
}: {
  oauthTokenHeader: string;
  queryId: string;
  websiteSharedUids: string[];
  type: string;
  dimension: string[];
}) {
  try {
    store.dispatch(setBudgetMobileIsLoading(true));
    const response =
      await bizDevelopmentDashboardMobileService.getBizMobileBudgetData({
        oauthTokenHeader,
        queryId,
        websiteSharedUids,
        type,
        dimension,
      });
    if (!response.status || response.status !== 200) {
      throw new Error();
    }
    store.dispatch(
      setBudgetBrandPerformanceLastRunData(response?.data?.data || []),
    );
  } catch (e: any) {
    store.dispatch(
      setDisplayErrorSnackBar({
        message:
          e?.parsedText ||
          "Failed to fetch brand performance last run query data. Please try again later or Refresh the page.",
        type: "error",
      }),
    );
  } finally {
    store.dispatch(setBudgetMobileIsLoading(false));
  }
}

// Be Aware - Fraud Players (reuses the same API call + query id as Desktop)
async function getBeAwareFraudPlayersData(
  oauthTokenHeader: string,
  siteSharedUids: string[],
) {
  try {
    store.dispatch(setBeAwareFraudPlayersMobileIsLoading(true));
    const response =
      await bizDevelopmentDashboardMobileService.getBeAwareFraudPlayersData(
        oauthTokenHeader,
        BIZ_BE_AWARE_QUERY_IDS.fraudPlayers,
        siteSharedUids,
      );
    if (!response || !response.status || response.status !== 200) {
      throw new Error("Failed to fetch biz development fraud players data");
    } else {
      store.dispatch(
        setBeAwareFraudPlayersMobileOutput(
          response?.data?.data?.query_output ?? [],
        ),
      );
    }
  } catch (e: any) {
    store.dispatch(
      setDisplayErrorSnackBar({
        message: e?.parsedText || "Unknown error occurred",
        type: "error",
      }),
    );
  } finally {
    store.dispatch(setBeAwareFraudPlayersMobileIsLoading(false));
  }
}

// Be Aware - Negative Balance (reuses the same API call + query id as Desktop)
async function getBeAwareNegativeBalanceData(
  oauthTokenHeader: string,
  siteSharedUids: string[],
) {
  try {
    store.dispatch(setBeAwareNegativeBalanceMobileIsLoading(true));
    const response =
      await bizDevelopmentDashboardMobileService.getBeAwareNegativeBalanceData(
        oauthTokenHeader,
        BIZ_BE_AWARE_QUERY_IDS.negativeBalance,
        siteSharedUids,
      );
    if (!response || !response.status || response.status !== 200) {
      throw new Error("Failed to fetch biz development negative balance data");
    } else {
      store.dispatch(
        setBeAwareNegativeBalanceMobileOutput(
          response?.data?.data?.query_output ?? [],
        ),
      );
    }
  } catch (e: any) {
    store.dispatch(
      setDisplayErrorSnackBar({
        message: e?.parsedText || "Unknown error occurred",
        type: "error",
      }),
    );
  } finally {
    store.dispatch(setBeAwareNegativeBalanceMobileIsLoading(false));
  }
}

// Be Aware - Conversions Upload (reuses the same query id + date window as Desktop)
async function getBeAwareConversionsUploadData(
  oauthTokenHeader: string,
  siteSharedUids: string[],
) {
  // Fixed review window shared with the filter's displayed date range.
  const dateFormat = "YYYY-MM-DD";
  const { start, end } = getConversionsUploadDateRange();
  const startDate = start.format(dateFormat);
  const endDate = end.format(dateFormat);
  try {
    store.dispatch(setBeAwareConversionsUploadMobileIsLoading(true));
    const response =
      await bizDevelopmentDashboardMobileService.getBeAwareConversionsUploadData(
        oauthTokenHeader,
        BIZ_BE_AWARE_QUERY_IDS.conversionsUpload,
        siteSharedUids,
        startDate,
        endDate,
      );
    if (!response || !response.status || response.status !== 200) {
      throw new Error(
        "Failed to fetch biz development conversions upload data",
      );
    } else {
      store.dispatch(
        setBeAwareConversionsUploadMobileOutput(
          response?.data?.data?.query_output ?? [],
        ),
      );
    }
  } catch (e: any) {
    store.dispatch(
      setDisplayErrorSnackBar({
        message: e?.parsedText || "Unknown error occurred",
        type: "error",
      }),
    );
  } finally {
    store.dispatch(setBeAwareConversionsUploadMobileIsLoading(false));
  }
}

export const BizDevelopmentDashboardMobileEndPointLogic = {
  getDashboardData,
  getComparisonDashboardData,
  getOverviewAccountSummaryDataInBizLeaderDashboardMobile,
  getDimensionInBizDashboardMobile,
  getOverviewConversionsMobileData,
  getOverviewConversionsTrafficSourcesMobile,
  getOverviewConversionsGeosMobile,
  getOverviewConversionsFilterOptionsDataForCampaignAdgroupKeyword,
  getOverviewConversionsActivityNotesMobile,
  getOverviewConversionsAllUserList,
  getBizMobileBudgetData,
  getBudgetCardBrandLastRunQueryInBizDevDashboardMobile,
  getBeAwareFraudPlayersData,
  getBeAwareNegativeBalanceData,
  getBeAwareConversionsUploadData,
};
